/**
 * L'écran de séance (`ARCHITECTURE_V2.md` §4.5, specs §12.3).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 LA SÉANCE ENTIÈRE À L'ÉCRAN — SUR LE MODÈLE DE HEVY
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Demandé après usage sur l'appareil *(29/08/2026)*. Le premier jet n'ouvrait
 * qu'**une série à la fois**, au motif que le §12.3 veut un écran « utilisable
 * d'une main » et qu'afficher trente formulaires ferait chercher le bon.
 *
 * **Le raisonnement était faux.** Ce qu'on veut voir en salle n'est pas « la
 * prochaine chose à faire » — on vient de la lire — mais **où l'on en est** :
 * ce qui reste, ce qui vient après, ce qu'on a mis à la série d'avant. Un
 * écran série par série oblige à se souvenir de tout ce qu'il cache.
 *
 * Il n'y a donc plus de « série courante » : chaque ligne se coche quand on
 * veut, dans l'ordre qu'on veut — ce qui est de toute façon ce qu'on fait.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 ÉCRIT HORS LIGNE D'EMBLÉE — LE SUBSTRAT, PAS UNE ROBUSTESSE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Le §1.2 le dit sans détour : 45 à 90 minutes d'interaction, ~30 mutations,
 * un réseau structurellement mauvais. « Un échec à la 80ᵉ minute coûte
 * l'accumulation de toute la séance, et rien ne la refait. » D'où :
 *
 *   - **rien n'attend le serveur** — plan déroulé localement, identités tirées
 *     par le client (§2.3-b), aperçu optimiste affiché ;
 *   - **l'aperçu est écrit dans le cache persisté** (`hooks/useSession.ts`),
 *     ce qui le fait survivre à une fermeture au milieu d'une séance ;
 *   - **aucun bandeau « hors ligne »** : c'est un mode nominal (§3.5).
 *
 * ⚠️ **Une séance terminée reste ENTIÈREMENT modifiable** — demandé le même
 * jour. C'est la même grille, les mêmes coches : corriger une charge trois
 * jours après ne demande aucun écran de plus. S'y ajoutent seulement « rouvrir »
 * et « supprimer ».
 */

import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import type { ActualSetDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { exerciseImageUrl } from '../../api/exercises';
import { ROUTINES_KEY, fetchRoutines } from '../../api/routines';
import { fetchLastPassage, lastPassageKey } from '../../api/sessions';
import { Button, Card } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { useSession } from '../../hooks/useSession';
import { useElapsed, useRestTimer, useWakeLock } from '../../hooks/useSessionClock';
import { newClientId } from '../../lib/dates';
import { ExercisePickerSheet } from './ExercisePickerSheet';
import { ExerciseSheet } from './ExerciseSheet';
import { BLOCK_KIND_LABEL } from './routineDraft';
import { SessionBlockCard, type RowHandlers } from './SessionBlockCard';
import { SessionTimers } from './SessionTimers';
import { groupBlocks, shouldRest, type SessionGroup } from './sessionGroups';
import { readValues, type SetValues } from './setValues';

/**
 * Repos par défaut quand la routine ne le dit pas.
 *
 * ⚠️ **Le repos n'est PAS copié sur la séance** (§4.2 ne copie que les cibles
 * qui comptent) : il est lu dans la routine, qui vit dans la réplique locale.
 * C'est cohérent avec ce qu'il est — une **prescription** qu'on lit au moment
 * de se reposer, pas un fait historique. Quand la routine a changé ou disparu,
 * on retombe ici plutôt que d'inventer une valeur d'époque.
 */
const REPOS_DEFAUT = 90;

/**
 * Amène au cache du service worker les **deux** vues de chaque exercice.
 *
 * 🔴 **Ciblé, jamais global** (§1.1) : 1 746 images à ≈ 60 Ko videraient le
 * quota de la PWA pour des exercices qu'on ne fait jamais. Une séance en compte
 * huit, soit ≈ 1 Mo — « ce qui rend la routine du jour consultable en salle
 * sans réseau ».
 *
 * ⚠️ **Ici, et pas dans la liste des routines** : le §1.1 range ce
 * préchargement avec l'historique du dernier passage, « préchargé à l'ouverture
 * de la routine » — or ce qui ouvre une routine *pour la faire*, c'est cet
 * écran. Le déclencher depuis la liste ferait entrer au cache les images de
 * toutes les routines qu'on survole.
 *
 * ⚠️ **`new Image()` sans `await`** : on ne veut ni bloquer l'ouverture, ni
 * traiter un échec. Une image non préchargée se chargera à l'affichage, et le
 * service worker la rangera dans `nutri-medias` de la même façon.
 */
function usePreloadedMedia(sets: readonly ActualSetDTO[]): void {
  const empreinte = sets
    .map((set) => `${set.exerciseMediaKey ?? ''}:${String(set.exerciseImageCount)}`)
    .join('|');

  useEffect(() => {
    const vues = new Map<string, number>();
    for (const set of sets) {
      if (set.exerciseMediaKey !== null && set.exerciseImageCount > 0) {
        vues.set(set.exerciseMediaKey, set.exerciseImageCount);
      }
    }
    for (const [mediaKey, count] of vues) {
      for (let index = 0; index < count; index += 1) {
        const image = new Image();
        image.src = exerciseImageUrl(mediaKey, index);
      }
    }
    /*
     * ⚠️ **Une empreinte, pas le tableau** : `sets` change d'identité à chaque
     * frappe dans un champ, et relancer le préchargement à chaque caractère
     * saisi ferait repartir huit requêtes par lettre.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- l'empreinte résume `sets`
  }, [empreinte]);
}

export function SessionScreen() {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const {
    session,
    query,
    saveHeader,
    saveSet,
    removeSet,
    removeSession,
    saveNote,
    removeNote,
    mutationError,
  } =
    useSession(clientId);

  const rest = useRestTimer();
  const [picking, setPicking] = useState(false);
  /*
   * ⚠️ **Le nom copié voyage avec l'adresse.** Un exercice supprimé n'a plus de
   * fiche, mais la série garde son nom (§4.2) — c'est lui qui titre la feuille
   * plutôt que de la laisser sans titre.
   */
  const [ficheFor, setFicheFor] = useState<{ clientId: string | null; name: string } | null>(null);
  /*
   * ⚠️ **La saisie en cours vit en `useState`, la séance dans le cache.** Ce
   * qui est tapé mais pas encore coché n'est pas un fait : le perdre à une
   * fermeture est sans conséquence, et l'écrire dans le cache persisté y
   * mettrait des valeurs que personne n'a validées.
   */
  const [valeurs, setValeurs] = useState<Record<string, SetValues>>({});
  /*
   * ⚠️ **Confirmation en deux temps, pas une boîte de dialogue.** Le projet
   * n'en a aucune, et `useConfirm` est un *flash visuel* de validation, pas une
   * question. Un second appui sur le même bouton suffit.
   *
   * ⚠️ **Et l'armement retombe tout seul au bout de cinq secondes** : un bouton
   * « Supprimer » qui resterait armé indéfiniment serait un piège pour le
   * prochain appui distrait, sur un écran qu'on garde ouvert une heure et demie.
   */
  const [confirmeSuppression, setConfirmeSuppression] = useState(false);
  const desarmement = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (desarmement.current !== null) window.clearTimeout(desarmement.current);
    },
    [],
  );

  const sets = useMemo(() => session?.sets ?? [], [session]);
  const blocs = useMemo(() => groupBlocks(sets), [sets]);
  usePreloadedMedia(sets);

  const elapsed = useElapsed(session?.startedAt ?? null, session?.finishedAt ?? null);
  const ouverte = session !== null && session.finishedAt === null;
  useWakeLock(ouverte);

  const faites = sets.filter((set) => set.status === 'done').length;

  /* --- Le dernier passage, pour TOUS les exercices ---------------------- */

  /*
   * ⚠️ **Préchargé pour tous, pas seulement l'exercice en cours** (§4.5). Le
   * charger à la demande marcherait à la maison et échouerait en salle, où le
   * réseau disparaît après le premier — le comportement intermittent que le
   * projet traque depuis l'étape 2.
   */
  const exercices = useMemo(
    () => [...new Set(sets.map((set) => set.exerciseClientId).filter((id) => id !== null))],
    [sets],
  );
  /*
   * 🔴 **La routine voyage avec le rappel, et c'est ce qui met la PROGRESSION
   * hors ligne** (V2 étape 7, §4.4). Le serveur y lit l'incrément prescrit et
   * rend la charge proposée dans la même réponse : aucun second appel à
   * précharger, aucun chemin qui échouerait en salle. La proposition hérite du
   * préchargement de l'étape 6 et de la persistance de la racine
   * `exercise-last` sans une ligne de plus.
   *
   * ⚠️ **Lue sur la séance, pas sur la routine chargée plus bas** : une séance
   * dont la routine a été supprimée garde `routineClientId`, et c'est le
   * serveur qui décidera qu'il n'y a plus rien à prescrire.
   */
  const routineClientId = session?.routineClientId ?? undefined;

  const passages = useQueries({
    queries: exercices.map((exerciseClientId) => ({
      queryKey: lastPassageKey(exerciseClientId, clientId, routineClientId),
      /*
       * ⚠️ **La séance en cours s'exclut elle-même** : sans quoi elle
       * deviendrait son propre « dernier passage » dès la première série
       * validée, et le rappel afficherait ce qu'on vient de faire.
       */
      queryFn: () => fetchLastPassage(exerciseClientId, clientId, routineClientId),
      staleTime: 5 * 60_000,
    })),
  });
  const rappelOf = (exerciseClientId: string | null) => {
    if (exerciseClientId === null) return undefined;
    const rang = exercices.indexOf(exerciseClientId);
    return rang === -1 ? undefined : passages[rang]?.data;
  };
  const passageOf = (exerciseClientId: string | null) =>
    rappelOf(exerciseClientId)?.passage ?? undefined;
  /**
   * La charge proposée pour cet exercice — `null` si rien n'est prescrit.
   *
   * ⚠️ **Par EXERCICE, jamais par série.** La double progression fait monter
   * *la charge de l'exercice* ; la calculer par série n'aurait aucun sens, et
   * la distribuer par série est exactement ce que fait `proposition()`, qui la
   * réserve aux séries de travail.
   */
  const progressionOf = (exerciseClientId: string | null) =>
    rappelOf(exerciseClientId)?.progression ?? null;

  /* --- Le repos, lu dans la routine ------------------------------------- */

  /*
   * ⚠️ **La même entrée de cache que la liste des routines**, déjà répliquée et
   * persistée (§3.1) : la lire ici ne coûte aucun aller-retour, et fonctionne
   * en salle. C'est la seule raison pour laquelle le repos peut être lu dans la
   * routine plutôt que copié sur la séance.
   */
  const routines = useQuery({ queryKey: ROUTINES_KEY, queryFn: fetchRoutines });
  const routine = routines.data?.find((row) => row.clientId === session?.routineClientId);

  const blocDeRoutine = (blockKey: string | null) =>
    blockKey === null ? undefined : routine?.blocks.find((b) => b.clientId === blockKey);

  /* Une étape par ligne — la forme de `recipe.steps` (étape 7). */
  const etapesEchauffement = (routine?.warmup ?? '')
    .split('\n')
    .map((ligne) => ligne.trim())
    .filter((ligne) => ligne !== '');

  const reposDe = (blockKey: string | null): number => {
    const bloc = blocDeRoutine(blockKey);
    if (!bloc) return REPOS_DEFAUT;
    /* Dans un bloc `single`, la ligne peut porter son propre repos (§4.3). */
    return bloc.restSeconds ?? bloc.lines[0]?.restSeconds ?? REPOS_DEFAUT;
  };

  /**
   * « Superset » ou « Circuit », **quand la routine le dit encore**.
   *
   * ⚠️ `actual_set` ne copie pas `kind` (§4.2 ne copie que ce qui compte) :
   * sur une séance dont la routine a changé ou disparu, on n'a rien à en dire
   * et la carte retombe sur « Enchaînement ». Ce n'est pas une perte —
   * l'information qui compte, l'**ordre**, est portée par les tours.
   */
  const natureDe = (blockKey: string | null): string | null => {
    const bloc = blocDeRoutine(blockKey);
    if (!bloc || bloc.kind === 'single') return null;
    return BLOCK_KIND_LABEL[bloc.kind];
  };

  /* --- Écriture ---------------------------------------------------------- */

  const corpsDe = (set: ActualSetDTO, extra: Partial<ActualSetDTO>) => ({
    exerciseClientId: set.exerciseClientId ?? '',
    exerciseName: set.exerciseName,
    blockKey: set.blockKey,
    position: set.position,
    /*
     * 🔴 **`extra` doit pouvoir le changer** *(07/09/2026)*. Il était codé en
     * dur sur `set.setIndex`, si bien que la renumérotation après suppression
     * passait un rang qui était **silencieusement ignoré** — et les numéros ne
     * bougeaient pas. Un champ d'`extra` qu'on jette sans rien dire est
     * exactement ce que le §5.1 refuse ailleurs (« refuser, pas écarter en
     * silence ») ; ici c'était pire, puisque rien ne le refusait non plus.
     */
    setIndex: extra.setIndex ?? set.setIndex,
    setKind: set.setKind,
    status: extra.status ?? set.status,
    reps: extra.reps ?? null,
    durationSeconds: extra.durationSeconds ?? null,
    weightKg: extra.weightKg ?? null,
    /*
     * ⚠️ **Le RPE se vide comme les autres.** Il retombait sur `set.rpe` du
     * temps où il n'était pas saisissable ; depuis qu'il l'est *(29/08/2026)*,
     * ce repli empêcherait de l'effacer — une valeur qu'on ne peut plus retirer
     * est une valeur qu'on finit par ne plus croire.
     */
    rpe: extra.rpe ?? null,
    targetRepsMin: set.targetRepsMin,
    targetRepsMax: set.targetRepsMax,
    targetDurationSeconds: set.targetDurationSeconds,
    targetWeightKg: set.targetWeightKg,
    targetRpe: set.targetRpe,
  });

  /**
   * Cocher — ou décocher — une série.
   *
   * 🔴 **Le repos est déclenché par le BLOC, pas par la ligne** (§4.3), et la
   * règle survit à l'ordre libre : `shouldRest` ne le lance que si plus aucun
   * autre exercice du même bloc n'a de série à faire à ce rang. Dans un
   * superset, valider la première ne déclenche donc toujours aucun repos.
   */
  const toggle = (set: ActualSetDTO) => {
    if (set.status === 'done') {
      saveSet.mutate({ setClientId: set.clientId, body: corpsDe(set, { status: 'pending' }) });
      return;
    }

    const precedente =
      passageOf(set.exerciseClientId)?.sets.find((row) => row.setIndex === set.setIndex) ??
      passageOf(set.exerciseClientId)?.sets.at(-1);
    /*
     * ⚠️ **La même proposition que le filigrane** — cf. `readValues`. La coche
     * ne doit jamais écrire une charge que le champ n'a pas montrée.
     */
    const lu = readValues(
      set,
      precedente,
      valeurs[set.clientId],
      progressionOf(set.exerciseClientId),
    );

    saveSet.mutate({ setClientId: set.clientId, body: corpsDe(set, { ...lu, status: 'done' }) });

    if (ouverte && shouldRest(sets, set)) rest.start(reposDe(set.blockKey));
  };

  const addSet = (groupe: SessionGroup) => {
    const modele = groupe.sets.at(-1);
    if (!modele) return;
    const rang = Math.max(...groupe.sets.map((s) => s.setIndex)) + 1;
    const position = Math.max(...sets.map((s) => s.position)) + 1;
    saveSet.mutate({
      setClientId: newClientId(),
      body: { ...corpsDe(modele, { status: 'pending' }), position, setIndex: rang },
    });
  };

  /**
   * Retirer la dernière série d'un exercice.
   *
   * ⚠️ **`skipped` plutôt qu'une suppression quand la série était PRÉVUE**
   * (§4.5 : « on veut savoir qu'elle était prévue »), et une vraie suppression
   * quand elle a été ajoutée à la main — une série qu'on vient de créer par
   * erreur n'a rien à raconter. La distinction se lit sur `status` : une série
   * jamais touchée et hors du plan initial est indiscernable ; on tranche donc
   * au plus sûr — `skipped` conserve, `DELETE` ne sert qu'aux séries au-delà du
   * plan, repérées par leur rang.
   */
  /**
   * Le même corps, mais **valeurs conservées** — pour un changement qui ne
   * touche pas à ce qu'on a fait.
   *
   * 🔴 **`corpsDe` remet `reps`, `durationSeconds`, `weightKg` et `rpe` à
   * `null` dès qu'`extra` ne les porte pas**, et c'est voulu : ses appelants
   * d'origine sont des **transitions d'état** (valider, passer, ajouter), où
   * ces valeurs sont soit fournies, soit à effacer.
   *
   * ⚠️ **La renumérotation n'en est pas une.** Elle change un rang, rien
   * d'autre — et sans ce relais, elle aurait **effacé le résultat de toutes les
   * séries suivantes** en les renumérotant. Défaut livré le 06/09 et jamais
   * déclenché, parce que le rang était de toute façon ignoré : les deux
   * défauts se masquaient l'un l'autre.
   */
  const corpsConserve = (set: ActualSetDTO, extra: Partial<ActualSetDTO>) =>
    corpsDe(set, {
      status: set.status,
      reps: set.reps,
      durationSeconds: set.durationSeconds,
      weightKg: set.weightKg,
      rpe: set.rpe,
      ...extra,
    });

  /**
   * Supprimer **une série désignée**, et renuméroter celles qui suivent.
   *
   * 🔴 **C'est un revirement sur le §4.5, et il est assumé** *(07/09/2026,
   * demandé : « pour enlever une série, on glisse la ligne vers la gauche ; les
   * numéros des autres séries de l'exo se mettent à jour »)*.
   *
   * Le bouton « − Série » écrivait `skipped` au premier appui et ne supprimait
   * qu'au second, au motif du §4.5 : « on veut savoir qu'elle était prévue ».
   * La demande de renumérotation tranche autrement — renuméroter n'a de sens
   * que si la série **disparaît**. ⚠️ **Conséquence à connaître : le statut
   * `skipped` n'est plus atteignable depuis l'écran.** Il reste au modèle, et
   * les séries déjà marquées ainsi s'affichent toujours barrées.
   *
   * ⚠️ **La renumérotation ne touche que ce qui a bougé** : on compare avant
   * d'écrire, donc supprimer la **dernière** série n'émet qu'une opération.
   * Sans cette garde, retirer la dernière de quatre en produirait quatre.
   *
   * ⚠️ **`setIndex` compte les séries de la LIGNE, tours confondus** (§ du
   * plan) : dans un enchaînement, les séries d'un même exercice se renumérotent
   * donc à travers les tours, ce que `groupe.sets` donne déjà dans l'ordre.
   */
  const removeSetAt = (set: ActualSetDTO) => {
    const groupe = blocs
      .flatMap((bloc) => bloc.exercises)
      .find((g) => g.sets.some((row) => row.clientId === set.clientId));
    if (!groupe) return;

    removeSet.mutate(set.clientId);

    const restantes = groupe.sets.filter((row) => row.clientId !== set.clientId);
    restantes.forEach((row, rang) => {
      const attendu = rang + 1;
      if (row.setIndex === attendu) return;
      saveSet.mutate({
        setClientId: row.clientId,
        body: corpsConserve(row, { setIndex: attendu }),
      });
    });
  };

  const addExercise = (exercise: {
    clientId: string;
    name: string;
    mediaKey: string | null;
    imageCount: number;
  }) => {
    const blockKey = newClientId();
    const base = sets.reduce((max, set) => Math.max(max, set.position), -1) + 1;
    for (let index = 0; index < 3; index += 1) {
      saveSet.mutate({
        setClientId: newClientId(),
        /* La vignette dès l'aperçu — cf. `PlannedSet.media`. */
        media: { mediaKey: exercise.mediaKey, imageCount: exercise.imageCount },
        body: {
          exerciseClientId: exercise.clientId,
          exerciseName: exercise.name,
          blockKey,
          position: base + index,
          setIndex: index + 1,
          setKind: 'work',
          status: 'pending',
          /*
           * ⚠️ Une cible est **exigée** (`measure_required`) : un exercice
           * ajouté à la volée n'en a pas, on pose donc la plage la plus neutre
           * possible plutôt que d'inventer une charge. « Rien de précis » est
           * une réponse honnête à « qu'est-ce qui était visé ».
           */
          targetRepsMin: 1,
          targetRepsMax: 20,
        },
      });
    }
    setPicking(false);
  };

  const cloture = (finishedAt: number | null) => {
    if (!session) return;
    saveHeader.mutate({
      date: session.date,
      routineClientId: session.routineClientId,
      routineName: session.routineName,
      startedAt: session.startedAt,
      finishedAt,
      notes: session.notes,
    });
    rest.stop();
  };

  /* --- Rendu -------------------------------------------------------------- */

  /*
   * 🔴 **« Ai-je quelque chose à montrer ? », jamais « y a-t-il une erreur ? »**
   * (§3.5). Une séance créée hors ligne n'existe pas encore côté serveur : sa
   * relecture répond 404 tant que la file n'a pas drainé. Tester l'erreur
   * d'abord afficherait « Séance introuvable » sur une séance en cours.
   */
  if (!session) {
    return (
      <section className="n-page">
        <ScreenHeader>
          <h1 className="screen-header__title">Séance</h1>
        </ScreenHeader>
        {query.isPending && <Hint>Chargement…</Hint>}
        <ReadNote error={readError(query)} quoi="la séance" />
      </section>
    );
  }

  const handlers: RowHandlers = {
    valeurs,
    onChange: (setClientId, v) => {
      setValeurs((courant) => ({ ...courant, [setClientId]: v }));
    },
    onToggle: toggle,
    precedenteDe: (set) =>
      passageOf(set.exerciseClientId)?.sets.find((row) => row.setIndex === set.setIndex) ??
      passageOf(set.exerciseClientId)?.sets.at(-1),
    progressionDe: (set) => progressionOf(set.exerciseClientId),
    onOpenExercise: (set) => {
      setFicheFor({ clientId: set.exerciseClientId, name: set.exerciseName });
    },
  };

  /**
   * 🔴 **La note du dernier passage** — celle qu'on s'est laissée la fois
   * d'avant, et la seule que l'écran affiche.
   *
   * ⚠️ **Le passage précédent, pas « la même routine »**, et l'écart est
   * assumé. Le retour demandait « la prochaine séance de la même routine » ;
   * `lastPassage` retient la dernière séance **portant cet exercice**, quelle
   * que soit la routine. Dans l'usage courant les deux coïncident — on refait
   * le même exercice dans la même routine — et là où ils divergent, la règle
   * large se trompe dans le bon sens : une note parle d'un **mouvement**
   * (« épaule droite sensible »), pas d'une séance. La montrer une fois de trop
   * ne coûte rien ; la cacher parce qu'on a changé de routine ferait perdre
   * exactement ce qu'on avait pris la peine d'écrire.
   */
  const notePrecedente = (exerciseClientId: string | null) => {
    const passage = passageOf(exerciseClientId);
    if (!passage || passage.note === null) return null;
    return { note: passage.note, date: passage.date };
  };

  const noteDe = (exerciseClientId: string | null): string =>
    exerciseClientId === null
      ? ''
      : (session.notesByExercise.find((row) => row.exerciseClientId === exerciseClientId)?.note ??
        '');

  return (
    <section className="n-page">
      <ScreenHeader>
        <h1 className="screen-header__title">{session.routineName ?? 'Séance libre'}</h1>
      </ScreenHeader>

      <SessionTimers
        elapsed={elapsed}
        rest={rest}
        done={faites}
        total={sets.length}
        onSkipRest={rest.stop}
      />

      {mutationError && <ErrorNote>{mutationError.message}</ErrorNote>}

      {!ouverte && (
        <Hint>
          Séance terminée le {session.date}. Elle reste modifiable : cochez, décochez, corrigez une
          charge.
        </Hint>
      )}

      {/*
        🔴 **L'échauffement, au-dessus des blocs** *(29/08/2026)*. Le mettre dans
        la routine et pas dans la séance en aurait fait une consigne qu'on lit
        la veille et qu'on ne suit jamais : c'est ici qu'on la suit, avant la
        première série.

        ⚠️ **Lu dans la routine, pas copié sur la séance.** C'est une
        **prescription**, comme le repos d'un bloc — pas un fait historique
        (§4.2 ne copie que les cibles qui comptent). Une routine dont
        l'échauffement a changé le montrera changé, ce qui est le bon
        comportement pour une consigne.
      */}
      {ouverte && etapesEchauffement.length > 0 && (
        <Card>
          <p className="n-eyebrow">Échauffement</p>
          <ol className="n-exo__steps">
            {etapesEchauffement.map((etape, index) => (
              <li key={index}>{etape}</li>
            ))}
          </ol>
        </Card>
      )}

      {blocs.length === 0 && (
        <p className="n-empty">
          Cette séance n’a aucun exercice. Ajoutez-en un ci-dessous.
        </p>
      )}

      {/*
        ⚠️ **Les gestionnaires de ligne voyagent en UN objet.** Chaque ligne en a
        besoin de quatre (saisie, changement, coche, série précédente) ; les
        passer un à un à travers deux niveaux de composants ferait six props par
        carte, dont l'ordre finirait par se mélanger.
      */}
      {blocs.map((bloc) => (
        <SessionBlockCard
          key={bloc.key}
          bloc={bloc}
          restSeconds={reposDe(bloc.blockKey)}
          kindLabel={natureDe(bloc.blockKey)}
          handlers={handlers}
          notes={noteDe}
          previousNotes={notePrecedente}
          disabled={false}
          onAddSet={addSet}
          onRemoveSet={removeSetAt}
          onNote={(exercice, texte) => {
            const cible = exercice.exerciseClientId;
            if (cible === null) return;
            /*
             * ⚠️ **Vider le champ SUPPRIME la note**, il n'en écrit pas une
             * vide : une note vide et pas de note ne doivent pas coexister en
             * base, sinon `notesByExercise` porterait des lignes qui
             * n'affichent rien.
             */
            if (texte === '') removeNote.mutate(cible);
            else saveNote.mutate({ exerciseClientId: cible, note: texte });
          }}
          onOpenExercise={(exercice) => {
            setFicheFor({ clientId: exercice.exerciseClientId, name: exercice.exerciseName });
          }}
        />
      ))}

      <Button
        variant="secondary"
        shape="rounded"
        block
        onClick={() => {
          setPicking(true);
        }}
      >
        Ajouter un exercice
      </Button>

      {ouverte ? (
        <Button
          variant="primary"
          size="lg"
          block
          disabled={saveHeader.isPending}
          onClick={() => {
            cloture(Date.now());
          }}
        >
          Terminer la séance
        </Button>
      ) : (
        <div className="n-session-footer">
          <Button
            variant="secondary"
            shape="rounded"
            block
            disabled={saveHeader.isPending}
            onClick={() => {
              cloture(null);
            }}
          >
            Rouvrir
          </Button>
          {/*
            ⚠️ **Confirmée**, contrairement au glissement du journal : une
            séance est une heure et demie de travail et trente lignes
            d'historique, pas une entrée de quinze secondes. Le geste
            destructeur non confirmé se justifie sur la seconde, pas sur la
            première.
          */}
          <Button
            variant="danger"
            shape="rounded"
            block
            disabled={removeSession.isPending}
            onClick={() => {
              if (!confirmeSuppression) {
                setConfirmeSuppression(true);
                if (desarmement.current !== null) window.clearTimeout(desarmement.current);
                desarmement.current = window.setTimeout(() => {
                  setConfirmeSuppression(false);
                  desarmement.current = null;
                }, 5000);
                return;
              }
              removeSession.mutate(undefined, {
                onSuccess: () => {
                  void navigate('/musculation/seances');
                },
              });
            }}
          >
            {confirmeSuppression ? 'Confirmer la suppression' : 'Supprimer'}
          </Button>
        </div>
      )}

      {/*
        🔴 **La fiche de l'exercice, en feuille** *(29/08/2026, après usage)* :
        « quand je clique sur le nom d'un exo ou sa vignette, affiche les deux
        images et sa description ». C'est la question la plus banale qu'on se
        pose en salle, et il fallait jusque-là quitter la séance pour y
        répondre.
      */}
      <ExerciseSheet
        clientId={ficheFor?.clientId ?? null}
        fallbackName={ficheFor?.name ?? ''}
        onClose={() => {
          setFicheFor(null);
        }}
      />

      <ExercisePickerSheet
        open={picking}
        onPick={addExercise}
        onClose={() => {
          setPicking(false);
        }}
      />

    </section>
  );
}
